/**
 * @fileoverview Accelerator icon selector for company displays
 * @active CONFIRMED - This component is actively used in the application
 * 
 * This utility function selects and renders the appropriate accelerator icon (Y Combinator, Techstars, etc.)
 * based on the company's accelerator information. It supports both direct and nested data structures.
 * 
 * Used by the following components:
 * @see {@link src/components/companies/companiesList/newCompaniesList/companiesCards/companyCardItem/AcceleratorCardView.tsx} - Card view implementation
 * @see {@link src/components/companies/companiesList/newCompaniesList/companiesTable/shared/AcceleratorListView.tsx} - Table view implementation
 * @see {@link src/components/companies/companiesList/newCompaniesList/companiesCards/companyCardItem/AcceleratorDetailPage.tsx} - Detail page implementation
 */

import { type Route } from "next";
import Link from "next/link";

import AntlerSvg from "@/assets/svg/accelerators/antler.svg";
import Logo500Svg from "@/assets/svg/accelerators/logo-500.svg";
import TechStarsSvg from "@/assets/svg/accelerators/techstars.svg";
import YcombinatorSvg from "@/assets/svg/accelerators/y-combinator.svg";

/**
 * Interface for company data that may contain accelerator information
 * This interface supports multiple data structures that might be passed to the function
 */
interface CompanyData {
  // Direct accelerator property with incubator_name
  accelerator?: {
    incubator_name?: string;
    slug?: string;
  } | Array<{
    id?: string;
    slug?: string;
  }>;
  
  // Nested accelerator within initialData (common in API responses)
  initialData?: {
    accelerator?: {
      incubator_name?: string;
      slug?: string;
    } | Array<{
      id?: string;
      slug?: string;
    }>;
    webUrl?: {
      urlYcombinator?: string;
    };
  };
  
  // Direct webUrl property
  webUrl?: {
    urlYcombinator?: string;
  };
}

/**
 * Selects and renders the appropriate accelerator icon based on company data
 * 
 * @param {Object} params - Function parameters
 * @param {CompanyData} params.company - Company data containing accelerator information
 * @param {boolean} [params.state=false] - Whether to render as a link or static icon
 * @returns {JSX.Element|null} The accelerator icon component or null if no match
 */
export const acceleratorSelector = ({ 
  company, 
  state = false 
}: { 
  company: CompanyData; 
  state?: boolean;
}) => {
  // Debug the incoming company data structure with more details
  console.log('acceleratorSelector received:', { 
    company, 
    state,
    hasAccelerator: Boolean(company?.accelerator),
    hasInitialData: Boolean(company?.initialData),
    hasInitialDataAccelerator: Boolean(company?.initialData?.accelerator),
    acceleratorType: company?.accelerator ? (Array.isArray(company.accelerator) ? 'array' : typeof company.accelerator) : 'undefined',
    initialDataAcceleratorType: company?.initialData?.accelerator ? (Array.isArray(company.initialData.accelerator) ? 'array' : typeof company.initialData.accelerator) : 'undefined'
  });
  
  // Extract accelerator data, handling both direct and nested structures
  const acceleratorData = company?.accelerator || company?.initialData?.accelerator;
  const links = company?.webUrl || company?.initialData?.webUrl;
  
  // Extract the accelerator name/slug from the data structure
  let item: string | undefined;
  
  if (Array.isArray(acceleratorData)) {
    // Handle array of accelerators
    item = acceleratorData[0]?.slug;
    console.log('Extracted accelerator from array:', {
      firstItem: acceleratorData[0],
      slug: acceleratorData[0]?.slug,
      length: acceleratorData.length
    });
  } else if (acceleratorData) {
    // Handle single accelerator object
    item = acceleratorData.incubator_name || acceleratorData.slug;
    console.log('Extracted accelerator from object:', {
      incubator_name: acceleratorData.incubator_name,
      slug: acceleratorData.slug,
      keys: Object.keys(acceleratorData)
    });
  }
  
  // Debug the extracted item value
  console.log('acceleratorSelector item value:', item);
  
  // Check if item exists and is a string before proceeding
  if (!item) {
    console.log('No accelerator name/slug found');
    return null;
  }
  
  const isTechStars = typeof item === 'string' && item.toLowerCase().includes("techstars");
  const isYcombinator = typeof item === 'string' && item.toLowerCase().includes("y combinator");
  const isAntler = typeof item === 'string' && item.toLowerCase().includes("antler");
  const is500 = typeof item === 'string' && item.toLowerCase().includes("500");
  
  // Debug which accelerator was detected
  console.log('Accelerator detection:', { isTechStars, isYcombinator, isAntler, is500 });

  const styleLink = {
    style: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
  };

  if (isYcombinator) {
    return !state ? (
      <Link
        href={"/investors/firms/y-combinator-mountain-view-ca-usa" as Route}
        {...styleLink}
      >
        <YcombinatorSvg />
      </Link>
    ) : (
      <YcombinatorSvg />
    );
  }
  if (isTechStars) {
    return !state ? (
      <Link 
        href={"/investors/firms/techstars-boulder-co-us" as Route} 
        {...styleLink}
      >
        <TechStarsSvg height="16" width="75" />
      </Link>
    ) : (
      <TechStarsSvg height="16" width="75" />
    );
  }
  if (isAntler) {
    return !state ? (
      <Link
        href={links?.urlYcombinator || "/" as Route}
        {...styleLink}
      >
        <AntlerSvg />
      </Link>
    ) : (
      <AntlerSvg />
    );
  }
  if (is500) {
    return !state ? (
      <Link
        href={"/" as Route}
        {...styleLink}
      >
        <Logo500Svg />
      </Link>
    ) : (
      <Logo500Svg />
    );
  }

  return null;
};
